import Chat from './Chat.jsx';
import { INSPECTOR, clampInspector } from './usePrefs.js';
import { useResizer } from './useResizer.js';
import { fmt, goalPct, readTime, wc } from './util.js';

/* The one sidebar.
 *
 * Write, Edit and ✨ are three things to see about the same writing, so they are
 * three tabs of one column rather than columns of their own. The conversation
 * is not a second inspector; it is what the inspector is showing.
 */

const gradeColor = g => (!g ? 'var(--muted-3)' : g <= 9 ? 'var(--good)' : g <= 12 ? 'var(--warn)' : 'var(--danger)');
const gradeNote = g => (!g ? 'Write something first.'
  : g <= 6 ? 'Very readable.' : g <= 9 ? 'Good.' : g <= 12 ? 'A bit dense.' : 'Hard going.');

function Stat({ kind, n, children }) {
  return <div className={'stat stat-' + kind}><b>{n}</b>{children}</div>;
}

export default function Inspector({ nav, go, space, doc, analysis, width, onWidth }) {
  const [shown, grip] = useResizer({
    width, onWidth, limits: INSPECTOR, clamp: clampInspector,
    edge: 'left', className: 'inspector-grip', label: 'Resize the sidebar'
  });
  // Write and Edit are ways of looking at the open document, so they only mean
  // something on the page. Beside the corkboard the column is the conversation.
  const onPage = nav.tool === 'write';
  const asking = nav.ask && !!space;
  const words = doc ? wc(doc.content) : 0;
  const pct = goalPct(space);
  const a = analysis;

  return (
    <aside className="inspector" style={{ width: shown + 'px' }}>
      {grip}
      <div className="inspector-body">
        {onPage ? (
          <div className="seg" role="tablist">
            <button
              className={!asking && nav.mode === 'write' ? 'is-active' : ''}
              onClick={() => go({ mode: 'write', ask: false })}
            >Write</button>
            <button
              className={!asking && nav.mode === 'edit' ? 'is-active' : ''}
              onClick={() => go({ mode: 'edit', ask: false })}
            >Edit</button>
            <button
              className={'seg-ask' + (asking ? ' is-active' : '')}
              title="Ask Claude about this space" aria-label="Ask Claude"
              aria-pressed={asking}
              onClick={() => go({ ask: !nav.ask })}
            >&#10024;</button>
          </div>
        ) : (
          <div className="inspector-head">
            <span className="inspector-title">&#10024; {space?.name || 'This space'}</span>
            <button className="icon-x" aria-label="Close the sidebar" onClick={() => go({ ask: false })}>×</button>
          </div>
        )}

        {asking ? <Chat key={space.id} space={space} doc={doc} /> : (
          <>
            {nav.mode === 'edit' && (
              <>
                <div className="insp-block readability">
                  <div className="insp-head">Readability</div>
                  <div className="grade" style={{ color: gradeColor(a?.grade) }}>
                    {a?.grade ? 'Grade ' + a.grade : '—'}
                  </div>
                  <div className="grade-note">{gradeNote(a?.grade)}</div>
                </div>
                <div className="insp-block stats">
                  <Stat kind="vhard" n={a?.vhard ?? 0}>of {a?.sents ?? 0} sentences are very hard to read.</Stat>
                  <Stat kind="hard" n={a?.hard ?? 0}>of {a?.sents ?? 0} sentences are hard to read.</Stat>
                  <Stat kind="adverb" n={a?.adverbs ?? 0}>adverbs. Fewer is stronger.</Stat>
                  <Stat kind="passive" n={a?.passive ?? 0}>uses of passive voice.</Stat>
                </div>
              </>
            )}

            <div className="insp-block counts">
              <div className="insp-head">Words: {fmt(words)}</div>
              <div className="read-time">{readTime(words)}</div>
            </div>

            <div className="insp-block goal">
              <div className="goal-row">
                <span className="goal-label">Space goal</span>
                <span className="goal-pct">{pct}%</span>
              </div>
              <div className="track"><div style={{ width: pct + '%' }} /></div>
            </div>
          </>
        )}
      </div>
    </aside>
  );
}
